import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Database, Download, Upload, Trash2, Plus, Loader2, Cloud, HardDrive, Shield } from 'lucide-react';
import { API_URL } from './AdminShared';

export const BackupTab = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { loadBackups(); }, []);

  const loadBackups = async () => {
    setLoading(true);
    try { const r = await adminAPI.getBackups(); setBackups(r.data); }
    catch { toast.error('Erreur chargement backups'); }
    setLoading(false);
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const r = await adminAPI.createBackup();
      toast.success(r.data.message);
      loadBackups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setCreating(false);
  };

  const downloadBackup = async (filename) => {
    try {
      const r = await adminAPI.downloadBackup(filename);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement lancé');
    } catch { toast.error('Erreur téléchargement'); }
  };

  const confirmRestore = async (filename) => {
    try {
      const r = await adminAPI.restoreBackup(filename);
      toast.success(r.data.message);
      setRestoring(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); setRestoring(null); }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Supprimer le backup ${filename} ?`)) return;
    try { await adminAPI.deleteBackup(filename); toast.success('Backup supprimé'); loadBackups(); }
    catch { toast.error('Erreur'); }
  };

  const exportAtlas = async () => {
    setExporting(true);
    try {
      const r = await adminAPI.exportToAtlas();
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setExporting(false);
  };

  return (
    <>
      <div className="space-y-6" data-testid="admin-backups">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2"><Database className="w-5 h-5 text-[#00FF00]" /> Sauvegardes</h3>
            <p className="text-xs text-slate-500 mt-1">Backup automatique chaque dimanche à 3h UTC. Maximum 10 backups conservés.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={createBackup} disabled={creating} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="create-backup-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer un backup
            </Button>
            <Button onClick={exportAtlas} disabled={exporting} variant="outline" className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10" data-testid="export-atlas-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Cloud className="w-4 h-4 mr-2" />}
              Exporter vers Atlas
            </Button>
          </div>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] p-4">
            <div className="flex items-center gap-2 mb-2"><HardDrive className="w-4 h-4 text-[#00FF00]" /><span className="text-sm font-bold text-white">Backup local</span></div>
            <p className="text-xs text-slate-500">Les backups sont stockés sur le serveur. Téléchargez-les régulièrement pour une copie externe.</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] p-4">
            <div className="flex items-center gap-2 mb-2"><Cloud className="w-4 h-4 text-blue-400" /><span className="text-sm font-bold text-white">MongoDB Atlas</span></div>
            <p className="text-xs text-slate-500">Exportez vers votre cluster Atlas gratuit (512 Mo). Configurez MONGO_ATLAS_URI dans les variables d'environnement.</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] p-4">
            <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-yellow-400" /><span className="text-sm font-bold text-white">Automatique</span></div>
            <p className="text-xs text-slate-500">Backup auto chaque dimanche. 4 dernières sauvegardes conservées. Backups manuels : 10 max.</p>
          </div>
        </div>

        {/* Backup list */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00FF00]" /></div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 mx-auto text-[#2A2A2A] mb-3" />
              <p className="text-slate-600">Aucun backup disponible</p>
              <p className="text-xs text-slate-700 mt-1">Créez votre premier backup maintenant</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#111] border-b border-[#2A2A2A]">
                <tr>
                  {['Fichier', 'Taille', 'Date', 'Type', 'Actions'].map(h => (
                    <th key={h} className="text-left p-4 font-semibold text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.filename} className="border-b border-[#1A1A1A] hover:bg-[#222]" data-testid={`backup-${b.filename}`}>
                    <td className="p-4 font-mono text-xs text-slate-300">{b.filename}</td>
                    <td className="p-4 text-slate-400">{b.size_mb} MB</td>
                    <td className="p-4 text-slate-400 text-xs">{new Date(b.created_at).toLocaleString('fr-FR')}</td>
                    <td className="p-4">
                      <Badge className={b.filename.includes('auto') ? 'bg-blue-500/15 text-blue-400 border-0 text-xs' : 'bg-[#00FF00]/15 text-[#00FF00] border-0 text-xs'}>
                        {b.filename.includes('auto') ? 'Auto' : 'Manuel'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadBackup(b.filename)} className="text-[#00FF00] h-8" data-testid={`download-${b.filename}`}><Download className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setRestoring(b.filename)} className="text-blue-400 h-8" data-testid={`restore-${b.filename}`}><Upload className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteBackup(b.filename)} className="text-red-400 h-8" data-testid={`delete-backup-${b.filename}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={!!restoring} onOpenChange={(open) => { if (!open) setRestoring(null); }}>
        <AlertDialogContent className="bg-[#1A1A1A] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Restaurer la base de données ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Cette action va <strong className="text-red-400">remplacer toutes les données actuelles</strong> par celles du backup <strong className="text-white">{restoring}</strong>. Cette opération est irréversible. Créez un backup de sécurité avant de continuer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#2A2A2A] text-slate-300 border-[#333] hover:bg-[#333]">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmRestore(restoring)} className="bg-red-600 text-white hover:bg-red-700 font-bold">Restaurer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
